import React, { useEffect, useRef, useState } from 'react'
import { RotateCw } from 'lucide-react'

const PullToRefresh = ({ children }) => {
  const [pullChange, setPullChange] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  // 戻りアニメーション中だけ transform を残す（常時 transform があると iOS でキャレット消失・入力時に最下部へ飛ぶ）
  const [settling, setSettling] = useState(false)
  const refreshThreshold = 80

  const startYRef = useRef(0)
  const isDraggingRef = useRef(false)
  const pullChangeRef = useRef(0)
  const settleTimerRef = useRef(null)

  useEffect(() => {
    const handleTouchStart = (e) => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      if (scrollTop <= 5) { // Allow small tolerance near top
        startYRef.current = e.touches[0].screenY
        isDraggingRef.current = true
      }
    }

    const handleTouchMove = (e) => {
      if (!isDraggingRef.current) return

      const scrollTop = window.scrollY || document.documentElement.scrollTop

      // If user scrolls down significantly during drag, cancel
      if (scrollTop > 10) {
        isDraggingRef.current = false
        setPullChange(0)
        pullChangeRef.current = 0
        return
      }

      const currentPoint = e.touches[0].screenY
      const pullDistance = currentPoint - startYRef.current

      if (pullDistance > 0) {
        const damped = Math.pow(pullDistance, 0.8)
        setPullChange(damped)
        pullChangeRef.current = damped
      } else {
        // We are pushing up above start point
        setPullChange(0)
        pullChangeRef.current = 0
      }
    }

    const handleTouchEnd = () => {
      if (!isDraggingRef.current) return
      isDraggingRef.current = false

      if (pullChangeRef.current > refreshThreshold) {
        setRefreshing(true)
        setPullChange(refreshThreshold)
        window.location.reload()
      } else {
        setRefreshing(false)
        if (pullChangeRef.current > 0) {
          setSettling(true)
          // transitionend は次のタッチで戻りアニメが中断されると発火しないため、時間でも必ず解除する
          clearTimeout(settleTimerRef.current)
          settleTimerRef.current = setTimeout(() => setSettling(false), 400)
        }
        setPullChange(0)
        pullChangeRef.current = 0
      }
    }

    // Passive listeners for performance
    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchmove', handleTouchMove, { passive: true })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      clearTimeout(settleTimerRef.current)
    }
  }, [])

  return (
    <div className="min-h-full">
      <div
        style={{
          transform: (pullChange > 0 || settling || refreshing) ? `translateY(${pullChange}px)` : 'none',
          transition: !isDraggingRef.current ? 'transform 0.3s cubic-bezier(0,0,0.2,1)' : 'none'
        }}
        onTransitionEnd={(e) => { if (e.target === e.currentTarget) setSettling(false) }}
        className="relative"
      >
        {/* Loading Indicator */}
        <div
          className="absolute left-0 w-full flex justify-center items-center pointer-events-none"
          style={{
            height: '60px',
            top: '-60px',
            opacity: Math.min(pullChange / (refreshThreshold * 0.5), 1)
          }}
        >
          <div className="bg-gray-800/90 backdrop-blur-sm p-2 rounded-full shadow-lg border border-gray-700 z-50">
            <RotateCw
              className={`w-6 h-6 text-orange-500 ${refreshing ? 'animate-spin' : ''}`}
              style={{ transform: `rotate(${pullChange * 3}deg)` }}
            />
          </div>
        </div>
        {children}
      </div>
    </div>
  )
}

export default PullToRefresh
